import { MutationFunctionOptions, useMutation } from '@apollo/client';
import { Store } from '@website/Store';
import { AdminFeature } from '@website/components/admin/AdminFeature';
import { useConfirmation } from '@website/components/utilities/ConfirmationService';
import {
  BookingSpaceCancellationType,
  CancelBookingDocument,
  CancelBookingInput,
  CancelBookingMutation,
  CancellationOptionsQuery,
  UserBookingDetailsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { useContext } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { UseFormReturn, useForm } from 'react-hook-form';
import { SubmitButton } from '../../../../../forms/fields/SubmitButton';
import ContactUsToCancel from '../ContactUsToCancel';
import { CancellationDescription } from './CancellationDescription';
import { CancellationOptions } from './CancellationOptions';
import { RemainingBalance } from './RemainingBalance';
import { SpacesToCancelField } from './SpacesToCancelField';
import { filterSpaceCancellationPossible } from './utils/filterSpaceCancellationPossible';
import { getDefaultCancellationType } from './utils/getDefaultCancellationType';

/**
 * Check if the booking is active
 *
 * A booking is active if there is at least one space that is not cancelled.
 */
export function checkBookingActive(
  spaces: Pick<UserBookingDetailsQuery['booking']['spaces'][0], 'active'>[],
): boolean {
  return !!spaces?.some((space) => space.active === true);
}

/**
 * Cancellation aim (used to add explainer text)
 */
export enum CancelAim {
  CANCEL = 'CANCEL',
  CHANGE_DATES = 'CHANGE_DATES',
}

/**
 * Cancellation form values
 */
export interface CancellationFormValues {
  bookingID: string;
  spaces: string;
  type: BookingSpaceCancellationType;
  aim: CancelAim;
}

type CancelBookingActionsProps = {
  booking: CancellationOptionsQuery['booking'];
  setValue: UseFormReturn<CancellationFormValues>['setValue'];
  watch: UseFormReturn<CancellationFormValues>['watch'];
  register: UseFormReturn<CancellationFormValues>['register'];
  spacesAvailableToCancel: Array<{
    id: string;
    cancellationOptions: CancellationOptionsQuery['booking']['spaces'][0]['cancellationOptions'];
  }>;
  isSubmitting: boolean;
};

function CancelBookingActions({
  booking,
  setValue,
  watch,
  register,
  spacesAvailableToCancel,
  isSubmitting,
}: CancelBookingActionsProps) {
  return (
    <>
      <div id="cancel-or-change-dates" className="mb-4">
        <h6 className="mb-4">
          Hi {booking.user.givenName}! What are you looking to do today?
        </h6>

        <>
          <Button
            className="me-1 mb-1"
            onClick={() => setValue('aim', CancelAim.CANCEL)}
            variant={
              watch('aim') === CancelAim.CANCEL ? 'primary' : 'outline-primary'
            }
          >
            Cancel
          </Button>

          <Button
            className="mb-1"
            onClick={() => setValue('aim', CancelAim.CHANGE_DATES)}
            variant={
              watch('aim') === CancelAim.CHANGE_DATES
                ? 'primary'
                : 'outline-primary'
            }
          >
            Change dates/trip
          </Button>
        </>
      </div>

      <div id="number-of-spaces" className="mb-4">
        <h6 className="mb-4">How many spaces would you like to do this for?</h6>

        <Form.Select
          {...register('spaces')}
          className="w-auto"
          aria-label="How many spaces?"
        >
          <SpacesToCancelField
            spacesAvailable={spacesAvailableToCancel.length}
          />
        </Form.Select>
      </div>

      <CancellationDescription
        cancellationOptions={spacesAvailableToCancel[0].cancellationOptions}
        watch={watch}
      />

      <RemainingBalance booking={booking} watch={watch} />

      {watch('aim') && (
        <>
          <h6 className="mt-4">Complete cancellation</h6>

          <p>
            Please check you have understood all of the details above. By
            clicking confirm, you agree to accept this cancellation option.
            Please note that unfortunately once a cancellation is processed, it
            cannot be changed.
          </p>

          <SubmitButton
            isSubmitting={isSubmitting}
            text={
              parseInt(watch('spaces')) > 1
                ? `Cancel ${watch('spaces')} spaces`
                : 'Cancel space'
            }
          />
        </>
      )}
    </>
  );
}

export default function CancellationForm({
  booking,
}: {
  booking: CancellationOptionsQuery['booking'];
}) {
  const { state } = useContext(Store);

  const isAdmin = state.user?.roles.includes('admin');

  const confirm = useConfirmation();

  // Setup the mutation
  const [cancelBooking] = useMutation(CancelBookingDocument);

  // Filter spaces to those that have cancellation options
  const spacesAvailableToCancel = booking?.spaces.filter(
    filterSpaceCancellationPossible,
  );

  // Get the default cancellation type (note that all spaces will have the same, so we just pick from the first)
  const defaultCancellationType = getDefaultCancellationType(
    spacesAvailableToCancel[0]?.cancellationOptions,
  );

  // Submit handler
  async function onSubmit(
    values: CancellationFormValues,
    spacesAvailableToCancel1: Array<{ id: string }>,
    cancelBooking1: (
      options?: MutationFunctionOptions<
        CancelBookingMutation,
        { input: CancelBookingInput }
      >,
    ) => Promise<any>,
    setError: UseFormReturn<CancellationFormValues>['setError'],
  ) {
    const numberOfSpaces = parseInt(values.spaces);

    const input: CancelBookingInput = {
      bookingID: values.bookingID,
      spaces: spacesAvailableToCancel1
        .slice(0, numberOfSpaces)
        .map((i) => i.id), // Just take the first ones
      type: values.type,
    };

    try {
      await confirm({
        variant: 'danger',
        catchOnCancel: true,
        title: 'Are you sure you want to cancel this booking?',
        description:
          'By clicking confirm, you agree to accept this cancellation option. Please note that unfortunately once a cancellation is processed, it cannot be changed.',
        cancelBtnLabel: 'No',
        confirmBtnLabel: 'Confirm',
      });
      await cancelBooking1({ variables: { input } });
    } catch (e) {
      setError('type', {}); // Set an error so the form doesn't go to success status (doesn't matter where)
      if (e !== 'closeDialog') alert(e);
    }
  }

  // Setup react-hook-form
  const {
    formState: { isSubmitting, isSubmitSuccessful },
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = useForm<CancellationFormValues>({
    defaultValues: {
      bookingID: booking.id,
      spaces: spacesAvailableToCancel?.length.toString(),
      type: defaultCancellationType,
      aim: null,
    },
  });

  // Show success if successful
  if (isSubmitSuccessful) {
    return <Alert variant="success">Success</Alert>;
  }

  // Create isInFuture boolean
  const isInFuture =
    DateTime.now() <
    DateTime.fromISO(booking.group.start.utc, {
      zone: 'utc',
    });

  // Check if booking is active or totally cancelled and set variable
  const isBookingActive = checkBookingActive(booking.spaces);

  if (isInFuture && isBookingActive && !isSubmitSuccessful) {
    return (
      <>
        <Form
          onSubmit={handleSubmit((v) =>
            onSubmit(v, spacesAvailableToCancel, cancelBooking, setError),
          )}
        >
          <AdminFeature>
            <Form.Group controlId="type" className="mb-2">
              <h6>Cancellation type</h6>
              <p>
                You can choose something other than the default cancellation
                policy here. The options are no refund, a balance refund (which
                gives a credit for the deposit & refunds the balance payment if
                it has already been made), and a full refund (which refunds all
                payments and will return any credits used to the guest).
              </p>
              <Form.Select {...register('type')} className="w-auto">
                <CancellationOptions
                  cancellationOptions={
                    spacesAvailableToCancel[0].cancellationOptions
                  }
                />
              </Form.Select>
            </Form.Group>
          </AdminFeature>
          {booking.group.trip.preventGuestCancellation && !isAdmin ? (
            <ContactUsToCancel booking={booking} />
          ) : (
            <>
              {booking.group.trip.preventGuestCancellation ? (
                <AdminFeature>
                  <CancelBookingActions
                    booking={booking}
                    setValue={setValue}
                    watch={watch}
                    register={register}
                    spacesAvailableToCancel={spacesAvailableToCancel}
                    isSubmitting={isSubmitting}
                  />
                </AdminFeature>
              ) : (
                <CancelBookingActions
                  booking={booking}
                  setValue={setValue}
                  watch={watch}
                  register={register}
                  spacesAvailableToCancel={spacesAvailableToCancel}
                  isSubmitting={isSubmitting}
                />
              )}
            </>
          )}
        </Form>
      </>
    );
  }

  return (
    <Alert variant="warning">
      This booking cannot be cancelled as it is either in the past or has
      already been cancelled.
    </Alert>
  );
}
